import React, { useEffect, useState } from 'react'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import { ReactComponent as InfoIcon } from 'presentation/assets/icons/info-new.svg'
import { ReactComponent as PositiveIcon } from 'presentation/assets/icons/productivity-positive.svg'
import { ReactComponent as NegativeIcon } from 'presentation/assets/icons/productivity-negative.svg'
import { ReactComponent as EqualIcon } from 'presentation/assets/icons/productivity-equal.svg'
import { ReactComponent as ElipseRed } from 'presentation/assets/icons/elipse-red.svg'
import { ReactComponent as ElipseBlue } from 'presentation/assets/icons/elipse-blue.svg'
import { ReactComponent as ElipseGreen } from 'presentation/assets/icons/elipse-green.svg'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import { TooltipNew } from 'presentation/shared/components/TooltipNew'
import { ToolTipsTexts } from './toolTipsTexts'
import { ComparisonCard } from './Cards/ComparisonCard'
import { OtherIndicatorsCard } from './Cards/OtherIndicatorsCard'
import CalendarNew from 'presentation/shared/components/CalendarNew'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import * as S from './styles'
import { useServices } from 'presentation/hooks/use-services'
import { toast } from 'react-toastify'
import moment from 'moment-timezone'
import { isValidDate } from 'presentation/utils/validators/is-valid-data'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import { MyProductivityChart } from 'presentation/doctor/components/MyProductivityChart'
import { useStores } from 'presentation/hooks/use-stores'
import AutoCompleteNew from 'presentation/shared/components/AutoCompleteNew'
import { FormikProvider, useFormik } from 'formik'
import { Doctor } from 'domain/entities/doctor-model'
import { Specialty } from 'domain/usecases/specialties/load-specialties'
import { MySurgeries } from 'domain/entities/my-surgeries-model'
import { GraphComparisonCard } from './Cards/GraphComparisonCard'
import { ReactComponent as NoDataIcon } from 'presentation/assets/icons/no-data.svg'
import { ReactComponent as NoIndicatorData } from 'presentation/assets/illustrations/no-indicator-data.svg'
import CardNew from 'presentation/shared/components/CardNew'
import { AverageTimeCard } from './Cards/AverageTimeCard'

export type MyProductivityProps = {
  variant?:
    | 'positive'
    | 'negative'
    | 'equal'
    | 'indicatorPositive'
    | 'indicatorNegative'
    | 'indicatorEqual'
    | 'noData'
}

type Props = {
  doctors: Doctor[]
} & WithLoadingProps

export const MyProductivityLayout = WithLoading(({ setIsLoading }: Props) => {
  const doctorService = useServices().doctor
  const [surgeryInfo, setSurgeryInfo] = useState<MySurgeries | undefined>()
  const [selectedDate, setSelectedDate] = useState<Date>(
    moment().subtract(1, 'month').startOf('month').toDate() // last month initial value
  )
  const [specialties, setSpecialties] = useState<Specialty[]>([])
  const [doctors, setDoctors] = useState([]) as any
  const selectedPreviousMonth = moment(selectedDate).format('MMMM')
  const selectedPreviousYear = moment(selectedDate)
    .subtract(1, 'year')
    .format('YYYY')
  const [showCalendar, setShowCalendar] = useState<boolean>(false)

  const [doctorInfo, setDoctorInfo] = useState() as any

  const loadDoctorInfo = async () => {
    const doctor = await doctorService.loadDoctor(['doctor_id', 'specialty'])

    setSearchSpecialty(doctor?.specialty)
    formik.setFieldValue('specialty', doctor?.specialty)

    setDoctorInfo(doctor)
  }

  useEffect(() => {
    loadDoctorInfo()
  }, [])

  const handleGetSurgeryInfo = async (date: Date) => {
    if (!date || !doctorInfo?.doctor_id) return
    try {
      setIsLoading(true)
      const formattedDate = moment(date).format('YYYY-MM-DD').toString()

      const response = await doctorService.getMyProductivityInfo({
        doctor_id: doctorInfo.doctor_id,
        date: formattedDate
      })

      setSurgeryInfo({
        ...response
      })
    } catch (error: any) {
      setSurgeryInfo(undefined)
      toast.warning('Nenhuma informação encontrada.')
    } finally {
      setIsLoading(false)
    }
  }

  const compareNumberToVariant = (value: number) => {
    if (value < 0) {
      return 'negative'
    } else if (value === 0 || isNaN(Number(value))) {
      return 'equal'
    } else {
      return 'positive'
    }
  }

  const compareNumberToIcon = (value: number) => {
    if (value < 0) {
      return <NegativeIcon />
    } else if (value === 0 || isNaN(Number(value))) {
      return <EqualIcon />
    } else {
      return <PositiveIcon />
    }
  }

  const handleChangeDate = (date: Date | Date[]) => {
    if (Array.isArray(date)) {
      setSelectedDate(date[0])
    } else {
      setSelectedDate(date)
    }
    setShowCalendar(false)
  }

  const handleNavigationButtons = (navigation: 'prev' | 'next') => {
    let date = selectedDate

    navigation === 'prev'
      ? (date = moment(date).subtract(1, 'month').startOf('month').toDate())
      : (date = moment(date).add(1, 'month').startOf('month').toDate())

    setSelectedDate(date)
  }

  const compareResultsToVariant = (value: number, overallValue: number) => {
    if (value < overallValue) {
      return 'indicatorPositive'
    } else if (
      value === overallValue ||
      isNaN(Number(value)) ||
      isNaN(Number(overallValue))
    ) {
      return 'indicatorEqual'
    } else {
      return 'indicatorNegative'
    }
  }

  const growthRateToVariant = (growth: number) => {
    if (growth > 0) {
      return 'indicatorPositive'
    } else if (growth === 0) {
      return 'indicatorEqual'
    } else {
      return 'indicatorNegative'
    }
  }

  useEffect(() => {
    handleGetSurgeryInfo(selectedDate)
  }, [selectedDate, doctorInfo])

  const currentAccount = useStores().currentAccount.getCurrentAccount()

  const initialValues = {
    specialty: surgeryInfo?.specialty || '', // doctor.specialty || '',
    doctor: {
      doctor_id: currentAccount?.user?.id || '',
      name: currentAccount?.user?.name || ''
    }
  }

  const formik = useFormik({
    initialValues,
    onSubmit: () => {
      return
    }
  })

  const [searchDoctor, setSearchDoctor] = useState<string | undefined>(
    formik.values.doctor?.name
  )

  async function selectDoctor(doctor: Doctor) {
    try {
      setIsLoading(true)

      if (doctor) {
        setDoctorInfo(doctor)
        formik.setFieldValue('doctor', doctor)
        setSearchDoctor(doctor.name)

        const response = await doctorService.getMyProductivityInfo({
          doctor_id: doctor.doctor_id,
          date: moment(selectedDate).format('YYYY-MM-DD').toString()
        })

        setSurgeryInfo({
          ...response
        })
      } else {
        setSearchDoctor(undefined)
      }
    } catch (error) {
      setSurgeryInfo(undefined)
      toast.error('Nenhuma informação encontrada.')
    } finally {
      setIsLoading(false)
    }
  }

  const loadDoctorsBySpecialty = async (doctor_name: string) => {
    if (currentAccount.user.groups.includes('doctor_admin')) {
      const response = await doctorService.getDoctorSpecialty({
        doctor_name: doctor_name,
        specialty_name: searchSpecialty,
        page_number: 1,
        page_size: 10
      })

      setDoctors(response.data)
    }
  }

  const [searchSpecialty, setSearchSpecialty] = useState(
    formik.values.specialty
  ) as any

  const resetAutocompleteSpecialty = () => {
    formik.setFieldValue('specialty', undefined)
    setSearchSpecialty(undefined)
  }

  const specialtyServices = useServices().specialty

  const loadSpecialties = async (name: string) => {
    try {
      if (!name) {
        return
      }
      const specialties = await specialtyServices.loadSpecialties({
        fields: ['name', 'code'],
        name,
        pagination: { pageNumber: 1, pageSize: 50 }
      })
      setSpecialties(specialties.data)
    } catch (err: any) {
      toast.error(err.message)
    }
  }

  function selectSpecialty(specialty: Specialty) {
    if (specialty) {
      formik.setFieldValue('specialty', specialty)
      setSearchSpecialty(specialty?.name)

      formik.setFieldValue('doctor', '')
      setSearchDoctor('')
    } else {
      formik.setFieldValue('specialty', undefined)
    }
  }

  return (
    <>
      <HeaderNew actualPageTitle="Minhas cirurgias" />
      <ContainerNew noHeader style={{ paddingTop: '16px' }}>
        <HeadingNew as="h1" color="neutral800" size="large" weight="semiBold">
          Minhas Cirurgias
        </HeadingNew>
        <S.CalendarWrapper>
          <S.DateSelector>
            <button
              className="btn"
              type="button"
              onClick={() => handleNavigationButtons('prev')}
              // Data mínima para desabilitar o botão (?)
              // disabled={selectedDate <= moment('01/01/2024').toDate()}
            >
              <ArrowBackIosIcon className="icon" />
            </button>
            <div className="date" onClick={() => setShowCalendar(true)}>
              {moment(selectedDate).format('MMMM YYYY')}
            </div>
            <button
              className="btn"
              type="button"
              onClick={() => handleNavigationButtons('next')}
              disabled={
                selectedDate >=
                moment().subtract(1, 'month').startOf('month').toDate()
              }
            >
              <ArrowForwardIosIcon className="icon" />
            </button>
          </S.DateSelector>
          {showCalendar && (
            <S.CalendarSelector>
              <CalendarNew
                defaultView="year"
                maxDetail="year"
                defaultActiveStartDate={
                  isValidDate(
                    moment(selectedDate).format('DD/MM/YYYY').toString()
                  )
                    ? moment(selectedDate, 'DD/MM/YYYY').toDate()
                    : undefined
                }
                value={
                  isValidDate(
                    moment(selectedDate).format('DD/MM/YYYY').toString()
                  )
                    ? moment(selectedDate, 'DD/MM/YYYY').toDate()
                    : undefined
                }
                maxDate={moment().subtract(1, 'month').endOf('month').toDate()}
                onDateChange={handleChangeDate}
                formatMonth={(locale, date) => moment(date).format('MMM')}
              />
            </S.CalendarSelector>
          )}
        </S.CalendarWrapper>

        <FormikProvider value={formik}>
          <S.AutocompleteWrapper>
            {currentAccount.user.groups.includes('doctor_admin') && (
              <AutoCompleteNew
                name="specialty"
                icon={<SearchIcon />}
                iconPosition="insideLeft"
                placeholder="Digite a especialidade"
                label="Especialidade"
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                value={searchSpecialty || ''}
                onType={loadSpecialties}
                onInputChange={setSearchSpecialty}
                suggestions={specialties.map((specialty) => ({
                  label: specialty.name,
                  value: specialty
                }))}
                onSuggestionClick={(specialty) => selectSpecialty(specialty)}
                clear={() =>
                  formik.values.specialty && resetAutocompleteSpecialty()
                }
                required
                data-testid="select-specialty-autocomplete"
                debounceDelay={1000}
                // returnFullSuggestion
                minCharactersSearch={3}
              />
            )}

            <AutoCompleteNew
              name="doctor"
              placeholder="Busca por nome"
              label="Médico"
              value={searchDoctor || ''}
              labelOverlapping
              labelSize="xxsmall"
              labelWeight="semiBold"
              labelColor="neutral700"
              onType={(val) => loadDoctorsBySpecialty(val)}
              icon={<SearchIcon />}
              iconPosition="insideLeft"
              onInputChange={setSearchDoctor}
              suggestions={doctors.map((doctor: any) => ({
                label: `${doctor.name}`,
                value: doctor
              }))}
              onSuggestionClick={(doctor) => selectDoctor(doctor)}
              // clear={() =>
              //   formik.values.doctor?.doctor_id && resetAutocompleteDoctor()
              // }
              disabled={
                !currentAccount.user.groups.includes('doctor_admin') ||
                !searchSpecialty
              }
              required
              data-testid="select-doctor-autocomplete"
              debounceDelay={1000}
              minCharactersSearch={3}
              // returnFullSuggestion
            />

            {!currentAccount.user.groups.includes('doctor_admin') && (
              <AutoCompleteNew
                name="specialty"
                icon={<SearchIcon />}
                iconPosition="insideLeft"
                placeholder="Digite a especialidade"
                label="Especialidade"
                labelOverlapping
                labelSize="xxsmall"
                labelWeight="semiBold"
                labelColor="neutral700"
                value={searchSpecialty || ''}
                onType={loadSpecialties}
                disabled={!currentAccount.user.groups.includes('doctor_admin')}
                onInputChange={setSearchSpecialty}
                suggestions={specialties.map((specialty) => ({
                  label: specialty.name,
                  value: specialty
                }))}
                onSuggestionClick={(specialty) => selectSpecialty(specialty)}
                // clear={() =>
                //   formik.values.specialty && resetAutocompleteSpecialty()
                // }
                required
                data-testid="select-specialty-autocomplete"
                debounceDelay={1000}
                // returnFullSuggestion
                minCharactersSearch={3}
              />
            )}
          </S.AutocompleteWrapper>
        </FormikProvider>
        <HeadingNew
          as="h1"
          className="s-title"
          color="neutral800"
          size="medium"
          weight="bold"
          style={{
            marginTop: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          Painel geral
          <TooltipNew className="icon" text={ToolTipsTexts.MY_PRODUCTIVITY}>
            <InfoIcon />
          </TooltipNew>
        </HeadingNew>
        <SupportTextNew style={{ margin: '4px 0 8px' }}>
          Acompanhe a sua posição em relação aos outros médicos da Rede
        </SupportTextNew>
        <MyProductivityChart
          productivity={{
            lastPosition:
              surgeryInfo?.productivity?.minimum_productivity_medium_specialty,
            mediumPosition:
              surgeryInfo?.productivity?.media_productivity_medium_specialty,
            firstPosition:
              surgeryInfo?.productivity?.maximum_productivity_medium_specialty,
            userPosition: surgeryInfo?.productivity?.productivity_medium_doctor
          }}
          averageTicket={{
            lastPosition:
              surgeryInfo?.average_ticket?.minimum_ticket_medium_specialty,
            mediumPosition:
              surgeryInfo?.average_ticket?.media_ticket_medium_specialty,
            firstPosition:
              surgeryInfo?.average_ticket?.maximum_ticket_medium_specialty,
            userPosition: surgeryInfo?.average_ticket?.ticket_medium_doctor
          }}
          NPS={{
            lastPosition: surgeryInfo?.nps?.minimum_nps_medium_specialty,
            mediumPosition: surgeryInfo?.nps?.media_nps_medium_specialty,
            firstPosition: surgeryInfo?.nps?.maximum_nps_medium_specialty,
            userPosition: surgeryInfo?.nps?.nps_medium_doctor
          }}
        />
        <HeadingNew
          as="h1"
          className="s-title"
          color="neutral800"
          size="medium"
          weight="bold"
          style={{
            marginTop: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          Evolução individual
          <TooltipNew className="icon" text={ToolTipsTexts.MY_PRODUCTIVITY}>
            <InfoIcon />
          </TooltipNew>
        </HeadingNew>
        <SupportTextNew style={{ margin: '4px 0 8px' }}>
          Acompanhe a sua evolução individual na Rede Mater Dei de Saúde
        </SupportTextNew>
        <HeadingNew
          as="h3"
          className="s-title"
          color="neutral800"
          size="medium"
          weight="bold"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '16px'
          }}
        >
          Você em{' '}
          {moment(selectedDate).format('MMMM').charAt(0).toUpperCase() +
            moment(selectedDate).format('MMMM').slice(1)}{' '}
        </HeadingNew>
        <S.PerformanceGrid>
          <span>
            {moment(selectedDate).format('MMMM').charAt(0).toUpperCase() +
              moment(selectedDate).format('MMMM').slice(1)}{' '}
            em comparação ao mês anterior
            <TooltipNew
              className="icon"
              text={ToolTipsTexts.CURRENT_PERFORMANCE}
            >
              <InfoIcon />
            </TooltipNew>
          </span>

          <S.CardContainer>
            <ComparisonCard
              icon={
                surgeryInfo?.productivity
                  ?.comparison_productivity_medium_previous_month ? (
                  compareNumberToIcon(
                    surgeryInfo.productivity
                      .comparison_productivity_medium_previous_month
                  )
                ) : (
                  <NoDataIcon />
                )
              }
              title="Volume cirúrgico"
              value={
                surgeryInfo?.productivity
                  ?.comparison_productivity_medium_previous_month
                  ? surgeryInfo.productivity
                      .comparison_productivity_medium_previous_month
                  : 'Sem dados'
              }
              variant={
                surgeryInfo?.productivity
                  ?.comparison_productivity_medium_previous_month
                  ? compareNumberToVariant(
                      surgeryInfo.productivity
                        .comparison_productivity_medium_previous_month
                    )
                  : 'noData'
              }
            />
            <ComparisonCard
              icon={
                surgeryInfo?.average_ticket
                  ?.comparison_ticket_medium_previous_month ? (
                  compareNumberToIcon(
                    surgeryInfo.average_ticket
                      .comparison_ticket_medium_previous_month
                  )
                ) : (
                  <NoDataIcon />
                )
              }
              title="Ticket médio"
              value={
                surgeryInfo?.average_ticket
                  ?.comparison_ticket_medium_previous_month
                  ? surgeryInfo.average_ticket
                      .comparison_ticket_medium_previous_month
                  : 'Sem dados'
              }
              variant={
                surgeryInfo?.average_ticket
                  ?.comparison_ticket_medium_previous_month
                  ? compareNumberToVariant(
                      surgeryInfo.average_ticket
                        .comparison_ticket_medium_previous_month
                    )
                  : 'noData'
              }
            />
            <ComparisonCard
              icon={
                surgeryInfo?.nps?.comparison_nps_medium_previous_month ? (
                  compareNumberToIcon(
                    surgeryInfo.nps.comparison_nps_medium_previous_month
                  )
                ) : (
                  <NoDataIcon />
                )
              }
              title="NPS"
              value={
                surgeryInfo?.nps?.comparison_nps_medium_previous_month
                  ? surgeryInfo.nps.comparison_nps_medium_previous_month
                  : 'Sem dados'
              }
              variant={
                surgeryInfo?.nps?.comparison_nps_medium_previous_month
                  ? compareNumberToVariant(
                      surgeryInfo.nps.comparison_nps_medium_previous_month
                    )
                  : 'noData'
              }
            />
          </S.CardContainer>
        </S.PerformanceGrid>
        <S.PerformanceGrid>
          <span>
            {moment(selectedDate).format('MMMM').charAt(0).toUpperCase() +
              moment(selectedDate).format('MMMM').slice(1)}
            /{moment(selectedDate).format('YYYY')} em comparação a{' '}
            {selectedPreviousMonth}/{selectedPreviousYear}
            <TooltipNew
              className="icon"
              text={ToolTipsTexts.LAST_YEAR_PERFORMANCE}
            >
              <InfoIcon />
            </TooltipNew>
          </span>

          <S.CardContainer>
            <ComparisonCard
              icon={
                surgeryInfo?.productivity
                  ?.comparison_productivity_medium_previous_year ? (
                  compareNumberToIcon(
                    surgeryInfo.productivity
                      .comparison_productivity_medium_previous_year
                  )
                ) : (
                  <NoDataIcon />
                )
              }
              title="Volume cirúrgico"
              value={
                surgeryInfo?.productivity
                  ?.comparison_productivity_medium_previous_year
                  ? surgeryInfo.productivity
                      .comparison_productivity_medium_previous_year
                  : 'Sem dados'
              }
              variant={
                surgeryInfo?.productivity
                  ?.comparison_productivity_medium_previous_year
                  ? compareNumberToVariant(
                      surgeryInfo.productivity
                        .comparison_productivity_medium_previous_year
                    )
                  : 'noData'
              }
            />
            <ComparisonCard
              icon={
                surgeryInfo?.average_ticket
                  ?.comparison_ticket_medium_previous_year ? (
                  compareNumberToIcon(
                    surgeryInfo.average_ticket
                      .comparison_ticket_medium_previous_year
                  )
                ) : (
                  <NoDataIcon />
                )
              }
              title="Ticket médio"
              value={
                surgeryInfo?.average_ticket
                  ?.comparison_ticket_medium_previous_year
                  ? surgeryInfo.average_ticket
                      .comparison_ticket_medium_previous_year
                  : 'Sem dados'
              }
              variant={
                surgeryInfo?.average_ticket
                  ?.comparison_ticket_medium_previous_year
                  ? compareNumberToVariant(
                      surgeryInfo.average_ticket
                        .comparison_ticket_medium_previous_year
                    )
                  : 'noData'
              }
            />
            <ComparisonCard
              icon={
                surgeryInfo?.nps?.comparison_nps_medium_previous_year ? (
                  compareNumberToIcon(
                    surgeryInfo.nps.comparison_nps_medium_previous_year
                  )
                ) : (
                  <NoDataIcon />
                )
              }
              title="NPS"
              value={
                surgeryInfo?.nps?.comparison_nps_medium_previous_year
                  ? surgeryInfo.nps.comparison_nps_medium_previous_year
                  : 'Sem dados'
              }
              variant={
                surgeryInfo?.nps?.comparison_nps_medium_previous_year
                  ? compareNumberToVariant(
                      surgeryInfo.nps.comparison_nps_medium_previous_year
                    )
                  : 'noData'
              }
            />
          </S.CardContainer>
        </S.PerformanceGrid>

        <HeadingNew
          as="h1"
          className="s-title"
          color="neutral800"
          size="medium"
          weight="bold"
          style={{
            marginTop: '24px',
            marginBottom: '8px'
          }}
        >
          Produção cirúrgica
        </HeadingNew>
        <S.SurgicalProductionContainer>
          {surgeryInfo?.production_surgical_annual &&
          surgeryInfo.production_surgical_annual
            ?.production_annual_growth_rate &&
          surgeryInfo.production_surgical_annual
            ?.production_annual_previous_year &&
          surgeryInfo.production_surgical_annual?.production_annual_actual ? (
            <GraphComparisonCard
              title={`JANEIRO - ${moment(selectedDate)
                .format('MMMM')
                .toUpperCase()}`}
              value={
                surgeryInfo.production_surgical_annual
                  .production_annual_growth_rate === 0
                  ? 'Produção se manteve igual'
                  : `${surgeryInfo.production_surgical_annual.production_annual_growth_rate}%`
              }
              textInfo={
                surgeryInfo.production_surgical_annual
                  .production_annual_growth_rate === 0
                  ? 'Em comparação ao mesmo período no ano anterior'
                  : 'Sua produção anual em comparação ao mesmo período no ano anterior'
              }
              variant={growthRateToVariant(
                surgeryInfo.production_surgical_annual
                  .production_annual_growth_rate
              )}
              chartValues={{
                lastYear:
                  surgeryInfo.production_surgical_annual
                    .production_annual_previous_year,
                currentYear:
                  surgeryInfo.production_surgical_annual
                    .production_annual_actual
              }}
            />
          ) : (
            <CardNew
              background="neutral50"
              gap="8px"
              style={{
                border: 'solid 1px #bdbdbd',
                alignItems: 'center'
              }}
            >
              <NoIndicatorData />
              <p
                style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#00000099',
                  textAlign: 'center'
                }}
              >
                Ainda não há informações relacionadas a este indicador
              </p>
            </CardNew>
          )}
        </S.SurgicalProductionContainer>

        <HeadingNew
          as="h1"
          className="s-title"
          color="neutral800"
          size="medium"
          weight="bold"
          style={{
            marginTop: '24px',
            marginBottom: '8px'
          }}
        >
          Tempo médio
        </HeadingNew>
        <S.AverageLeadTimeCardContainer>
          {surgeryInfo?.lead_time &&
          surgeryInfo.lead_time.lead_time_authorized ? (
            <AverageTimeCard
              averageTime={surgeryInfo?.lead_time?.lead_time_authorized || 0}
              type="authorization"
            />
          ) : (
            <CardNew
              background="neutral50"
              gap="8px"
              style={{
                border: 'solid 1px #bdbdbd',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <NoIndicatorData />
              <p
                style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#00000099',
                  textAlign: 'center'
                }}
              >
                Ainda não há informações relacionadas a este indicador
              </p>
            </CardNew>
          )}
        </S.AverageLeadTimeCardContainer>
        <S.AverageLeadTimeCardContainer>
          {surgeryInfo?.lead_time &&
          surgeryInfo.lead_time.lead_time_concluded ? (
            <AverageTimeCard
              averageTime={surgeryInfo?.lead_time?.lead_time_concluded || 0}
              type="conclusion"
            />
          ) : (
            <CardNew
              background="neutral50"
              gap="8px"
              style={{
                border: 'solid 1px #bdbdbd',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <NoIndicatorData />
              <p
                style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#00000099',
                  textAlign: 'center'
                }}
              >
                Ainda não há informações relacionadas a este indicador
              </p>
            </CardNew>
          )}
        </S.AverageLeadTimeCardContainer>

        <HeadingNew
          as="h1"
          className="s-title"
          color="neutral800"
          size="medium"
          weight="bold"
          style={{
            marginTop: '24px',
            marginBottom: '8px'
          }}
        >
          Outros indicadores
        </HeadingNew>
        <S.OtherIndicatorsContainer>
          <OtherIndicatorsCard
            value={
              surgeryInfo?.others?.average_internment_time_doctor
                ? `${parseFloat(
                    surgeryInfo.others.average_internment_time_doctor.toFixed(1)
                  )} dias`
                : 'Sem dados'
            }
            title="Tempo médio internação"
            textInfo={
              surgeryInfo?.others?.average_internment_time_doctor
                ? `Pacientes ficam internados por cerca de ${parseFloat(
                    surgeryInfo.others.average_internment_time_doctor.toFixed(1)
                  )} dias em suas cirurgias`
                : 'Sem dados'
            }
            generalAverage={
              surgeryInfo?.others?.average_internment_time_specialty
                ? `${parseFloat(
                    surgeryInfo.others.average_internment_time_specialty.toFixed(
                      1
                    )
                  )} dias`
                : 'Sem dados'
            }
            variant={
              surgeryInfo?.others?.average_internment_time_doctor &&
              surgeryInfo?.others?.average_internment_time_specialty
                ? compareResultsToVariant(
                    parseFloat(
                      surgeryInfo.others.average_internment_time_doctor.toFixed(
                        1
                      )
                    ),
                    parseFloat(
                      surgeryInfo.others.average_internment_time_specialty.toFixed(
                        1
                      )
                    )
                  )
                : 'noData'
            }
          />
          <OtherIndicatorsCard
            value={
              surgeryInfo?.others?.delay_percentage_doctor
                ? `${parseFloat(
                    surgeryInfo.others.delay_percentage_doctor.toFixed(2)
                  )}%`
                : 'Sem dados'
            }
            title="Atraso cirúrgico"
            textInfo={
              surgeryInfo?.others?.quantity_done_doctor &&
              surgeryInfo?.others?.delay_quantity_doctor
                ? `A cada ${surgeryInfo.others.quantity_done_doctor} cirurgias realizadas, ${surgeryInfo.others.delay_quantity_doctor} cirurgias possuem atrasos`
                : 'Sem dados'
            }
            generalAverage={
              surgeryInfo?.others?.delay_percentage_specialty
                ? `${parseFloat(
                    surgeryInfo.others.delay_percentage_specialty.toFixed(2)
                  )}%`
                : 'Sem dados'
            }
            variant={
              surgeryInfo?.others?.delay_percentage_doctor &&
              surgeryInfo?.others?.delay_percentage_specialty
                ? compareResultsToVariant(
                    parseFloat(
                      surgeryInfo.others.delay_percentage_doctor.toFixed(2)
                    ),
                    parseFloat(
                      surgeryInfo.others.delay_percentage_specialty.toFixed(2)
                    )
                  )
                : 'noData'
            }
          />
          <OtherIndicatorsCard
            value={
              surgeryInfo?.others?.conversion_rate_doctor
                ? `${parseFloat(
                    surgeryInfo.others.conversion_rate_doctor.toFixed(2)
                  )}%`
                : 'Sem dados'
            }
            title="Taxa de conversão"
            textInfo={
              surgeryInfo?.others?.quantity_total_doctor &&
              surgeryInfo?.others?.quantity_pending_doctor
                ? `A cada ${surgeryInfo.others.quantity_total_doctor} pedidos cirúrgicos cadastrados, ${surgeryInfo.others.quantity_pending_doctor} são realizados`
                : 'Sem dados'
            }
            generalAverage={
              surgeryInfo?.others?.conversion_rate_specialty
                ? `${parseFloat(
                    surgeryInfo.others.conversion_rate_specialty.toFixed(2)
                  )}%`
                : 'Sem dados'
            }
            variant={
              surgeryInfo?.others?.conversion_rate_specialty &&
              surgeryInfo?.others?.conversion_rate_doctor
                ? compareResultsToVariant(
                    parseFloat(
                      surgeryInfo.others.conversion_rate_specialty.toFixed(2)
                    ),
                    parseFloat(
                      surgeryInfo.others.conversion_rate_doctor.toFixed(2)
                    )
                  )
                : 'noData'
            }
          />
          <OtherIndicatorsCard
            value={
              surgeryInfo?.others?.percentage_high_9h_doctor
                ? `${parseFloat(
                    surgeryInfo.others.percentage_high_9h_doctor.toFixed(2)
                  )}%`
                : 'Sem dados'
            }
            title="Alta em até 9h"
            textInfo={
              surgeryInfo?.others?.quantity_high_doctor &&
              surgeryInfo?.others?.quantity_high_9h_doctor
                ? `A cada ${surgeryInfo.others.quantity_high_doctor} cirurgias realizadas, ${surgeryInfo.others.quantity_high_9h_doctor} pacientes recebem alta em até 9h`
                : 'Sem dados'
            }
            generalAverage={
              surgeryInfo?.others?.percentage_high_9h_specialty
                ? `${parseFloat(
                    surgeryInfo.others.percentage_high_9h_specialty.toFixed(2)
                  )}%`
                : 'Sem dados'
            }
            variant={
              surgeryInfo?.others?.percentage_high_9h_doctor &&
              surgeryInfo?.others?.percentage_high_9h_specialty
                ? compareResultsToVariant(
                    parseFloat(
                      surgeryInfo.others.percentage_high_9h_doctor.toFixed(2)
                    ),
                    parseFloat(
                      surgeryInfo.others.percentage_high_9h_specialty.toFixed(2)
                    )
                  )
                : 'noData'
            }
          />
        </S.OtherIndicatorsContainer>
        <S.CardCaptionContainer>
          <S.CardCaption>
            <ElipseGreen /> Melhor que a média
          </S.CardCaption>
          <S.CardCaption>
            <ElipseBlue /> Igual à média
          </S.CardCaption>
          <S.CardCaption>
            <ElipseRed /> Pior que a média
          </S.CardCaption>
        </S.CardCaptionContainer>
      </ContainerNew>
    </>
  )
})
